import { useEffect, useMemo, useRef, useState } from 'react';
import { INDUSTRY_GROUPS } from '../wizard/wizardSteps';

export type FieldTone = 'empty' | 'demo' | 'owned' | 'quiet';

export function fieldInputClass(tone?: FieldTone): string {
  if (tone === 'empty') return 'fp-input fp-field-empty';
  if (tone === 'demo') return 'fp-input fp-field-demo';
  if (tone === 'owned') return 'fp-input fp-field-owned';
  return 'fp-input';
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  onCommit?: (value: string) => void;
  tone?: FieldTone;
  placeholder?: string;
}

export function IndustryPicker({
  value,
  onChange,
  onCommit,
  tone,
  placeholder = 'Type to filter, or write your own',
}: Props) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const listId = 'industry-picker-list';

  const groups = useMemo(() => {
    const q = value.trim().toLowerCase();
    if (!q) return INDUSTRY_GROUPS.map((g) => ({ ...g, items: [...g.items] }));
    return INDUSTRY_GROUPS
      .map((g) => ({
        label: g.label,
        items: g.items.filter((item) => item.toLowerCase().includes(q)),
      }))
      .filter((g) => g.items.length > 0);
  }, [value]);

  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open]);

  useEffect(() => {
    setHighlight(0);
  }, [value, open]);

  const commit = (next: string) => {
    onChange(next);
    setOpen(false);
    onCommit?.(next);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight((i) => Math.min(i + 1, Math.max(flat.length - 1, 0)));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      const pick = flat[highlight];
      if (open && pick) {
        e.preventDefault();
        commit(pick);
      }
    }
  };

  return (
    <div ref={boxRef} className="relative">
      <input
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => onCommit?.(value)}
        onKeyDown={onKeyDown}
        className={fieldInputClass(tone)}
      />
      {open && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-[12px] border border-nm-border bg-nm-card py-1 shadow-lg"
        >
          {groups.length === 0 ? (
            <li className="px-3 py-2 text-xs text-nm-muted">Keep typing — freeform is allowed.</li>
          ) : (
            groups.map((g) => (
              <li key={g.label}>
                <div className="px-3 pb-1 pt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-nm-tertiary">
                  {g.label}
                </div>
                {g.items.map((item) => {
                  const idx = flat.indexOf(item);
                  const on = idx === highlight;
                  return (
                    <button
                      key={item}
                      type="button"
                      role="option"
                      aria-selected={on}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => commit(item)}
                      className={`flex w-full px-3 py-1.5 text-left text-sm ${on ? 'bg-nm-surface text-nm-ink' : 'text-nm-ink hover:bg-nm-surface'}`}
                    >
                      {item}
                    </button>
                  );
                })}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
